import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Slider from 'react-infinite-logo-slider'
import { HomePageSection } from '../styled'
import { motion, useScroll, useTransform } from 'framer-motion'

import { Box } from '../../../shared_components/UI/Box'
import { VideoContainer } from '../../../shared_components/Hero'
import {
  Background,
  ForceFullWidth,
} from '../../../shared_components/Hero/styled'
import { selectHeaderHeight } from '../../../modules/miscUi/selectors'
import { useRegisterLeadMutation } from '../../../modules/user/api'
import {
  BarTitleBox,
  BaseVaultCont,
  BrandBackground,
  ChartBox,
  CountdownItemCont,
  CountdownLabel,
  CountdownValue,
  GradientBottom,
  HeroBrandBackground,
  MetricCont,
  PayoutBar,
  SectionTitleCont,
  StyledMetricItem,
  VaultCountItemCont,
  VaultCountValue,
  VaultHero,
  VaultHeroImagesCont,
  VaultItemBox,
  VaultItemText,
  VaultItemTitle,
  VaultsSoldBox,
  VaultsSoldText,
  VaultsToNextPriceBox,
  VaultsToNextPriceText,
} from './styles'

import { useHistory } from 'react-router-dom'
import {
  useGetContractByNameQuery,
  useGetNftDropsForGuestQuery,
} from '../../../modules/web3/api'
import useWeb3 from '../../../hooks/useWeb3'
import deepCopy from '../../../utils/deepCopy'
import TickUpNumber from '../../../shared_components/TickUpNumber'

import { USDC_DECIMALS } from '../../../config'
import { amountFromWei } from '../../../utils/amount'
import { NftDrop, NftDropCard } from '../../Dashboard/Vault/NftDrops'
import { useWindowSize } from '@uidotdev/usehooks'
import { BASE } from '../../../constants'
import { STANDARD_PAGE_MAX_WIDTH } from '../../../theme'
import {
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  Span,
} from '../../../shared_components/Library/Typeography'
import { HomeHero, ProductsHero } from '../HomeHero'
import styled from 'styled-components'
import { MaxWidth } from '../../../shared_components/Page'

export const isMaintenance = false
const maintenanceEndDate = new Date(Date.UTC(2024, 2, 28, 21, 59, 0))
const maintenanceTotalVaultsSold = 8000

const useTotalVaultsSold = ({ contract }) => {
  const { getReadOnlyContract } = useWeb3()
  const vaultSaleAbi = contract?.abi
  const vaultSaleAddress = contract?.address

  const [vaultsSold, setVaultsSold] = useState(0)

  const getVaultsSold = useCallback(async () => {
    if (!vaultSaleAbi || !vaultSaleAddress) return
    const vaultSaleContractInstance = getReadOnlyContract(
      vaultSaleAddress,
      deepCopy(vaultSaleAbi),
    )
    const total = await vaultSaleContractInstance.vaultCount()
    setVaultsSold(total)
  }, [vaultSaleAbi, vaultSaleAddress, getReadOnlyContract])

  useEffect(() => {
    if (!vaultSaleAbi || !vaultSaleAddress) return
    getVaultsSold()
  }, [getVaultsSold, vaultSaleAbi, vaultSaleAddress])

  return vaultsSold
}

const useCurrentVaultPrice = ({ contract }) => {
  const { getReadOnlyContract } = useWeb3()
  const vaultSaleAbi = contract?.abi
  const vaultSaleAddress = contract?.address

  const [vaultCurrentPrice, setVaultCurrentPrice] = useState(0)

  const getCurrentVaultPrice = useCallback(async () => {
    if (!vaultSaleAbi || !vaultSaleAddress) return
    const vaultSaleContractInstance = getReadOnlyContract(
      vaultSaleAddress,
      deepCopy(vaultSaleAbi),
    )
    const currentVaultPriceRes = await vaultSaleContractInstance.pricePerVault()
    setVaultCurrentPrice(currentVaultPriceRes['price'])
  }, [vaultSaleAbi, vaultSaleAddress])

  useEffect(() => {
    if (!vaultSaleAbi || !vaultSaleAddress) return
    getCurrentVaultPrice()
  }, [vaultSaleAbi, vaultSaleAddress, getCurrentVaultPrice])

  return amountFromWei(vaultCurrentPrice, USDC_DECIMALS).toNumber()
}

export default function Vault() {
  const headerHeight = useSelector(selectHeaderHeight)
  const [email, setEmail] = useState('')
  const [registerLead, { isLoading: isSubmittingLead, isSuccess, isError }] =
    useRegisterLeadMutation()

  const history = useHistory()

  const onEmailSubmit = useCallback(async () => {
    try {
      await registerLead({ email })
    } catch (e) {
      console.error(e)
      toast.error('An error occurred, please try again later')
    }
  }, [email, registerLead])

  const { data: vaultSaleContractData } = useGetContractByNameQuery({
    name: 'VaultSaleWithBase',
    chainId: BASE.chainId,
  })
  const totalVaultsSold = useTotalVaultsSold({
    contract: vaultSaleContractData?.contract,
  })
  const vaultCurrentPrice = useCurrentVaultPrice({
    contract: vaultSaleContractData?.contract,
  })
  const vaultCurrentPriceSafe = Math.max(vaultCurrentPrice, 1000)
  const vaultTier = Math.floor((vaultCurrentPrice - 1250) / 15) + 2
  const vaultTierSafe = Math.max(vaultTier, 1)

  const parallaxRef = useRef()
  const { scrollYProgress } = useScroll({ target: parallaxRef })
  const y = useTransform(scrollYProgress, [1, 0], [-10, 80])
  const x = useTransform(scrollYProgress, [1, 0], [-10, 80])

  return (
    <>
      <VaultHero>
        <ForceFullWidth>
          <MaxWidth className="vault-hero-content" maxWidth={1440}>
            <HeroBrandBackground>
              <img
                className="vault-lightning"
                src="/zap-vault-hero-lightning.png"
              />
            </HeroBrandBackground>
            <VaultHeroImagesCont>
              <motion.div style={{ y: y, x: x }}>
                <img className="vault-box" src="/zap-vault-hero-box.png" />
              </motion.div>
            </VaultHeroImagesCont>
            <BaseVaultCont>
              <Box ig dr padV="section">
                <Box g={20} dc mw="536px">
                  <H2 ff="Proxima Nova" s="4XL" c="text-invert" fw="bolder" uc>
                    Exclusive packages with powerful rewards
                  </H2>
                  <VaultsSoldBox>
                    <VaultsSold total={totalVaultsSold} />
                    {/* <VaultsToNextPrice total={totalVaultsSold} /> */}
                  </VaultsSoldBox>
                  <P s="XL" fw="semibold" c="text-invert">
                    ZAP VAULTS are exclusive bundles that unlock powerful
                    rewards and unparalleled benefits on the ZAP platform
                  </P>
                  {/*<ThemedButton*/}
                  {/*  $styleType="primary-invert"*/}
                  {/*  // disabled*/}
                  {/*  onClick={() => {*/}
                  {/*    history.push('/vault/checkout')*/}
                  {/*  }}*/}
                  {/*>*/}
                  {/*  Buy Vaults*/}
                  {/*</ThemedButton>*/}
                </Box>
              </Box>
            </BaseVaultCont>
          </MaxWidth>
          <span ref={parallaxRef} />
        </ForceFullWidth>
      </VaultHero>
      <HomePageSection>
        <ForceFullWidth>
          <VideoContainer $height="600px">
            <video autoPlay loop muted playsInline>
              <source src="/video/zap-vault-hero.mp4" type="video/mp4" />
              <Background
                backgroundUrl="/zap-vault-hero.png"
                height="600px"
                mobileH="416px"
              />
            </video>
            <GradientBottom />
          </VideoContainer>
        </ForceFullWidth>
        <MaxWidth maxWidth={STANDARD_PAGE_MAX_WIDTH}>
          <Box padding="220px 0 0 0">
            <Box g={48} padV="section" columnMobile>
              <SectionTitleCont dc g={16} centerMobile>
                <H2 s="S" c="text-brandPrimary">
                  Price metrics
                </H2>
                <H3 s="3XL" fw="bolder" c="text-primary" ff="Proxima Nova" uc>
                  You won't see this price again
                </H3>
              </SectionTitleCont>
              <MetricCont dc fw jc g={24}>
                <Box dc jc g={24} centerMobile>
                  <MetricItem title="Max available" value="30,000" />
                  <MetricItem
                    title="Current price"
                    text={`Tier ${vaultTierSafe}`}
                    value={`$${vaultCurrentPriceSafe}`}
                  />
                  <MetricItem
                    title="Price increase / 200 VAULTS"
                    text="from 10,200 Vaults"
                    value="+$15"
                  />
                </Box>
              </MetricCont>
            </Box>
          </Box>
          <div>
            <Box igmv dc g={12}>
              <Box dc g={20}>
                <BarTitleBox g={6} ac>
                  <H4 s="M" fw="semibold" ls="-0.5px">
                    Daily ZAP reward payout
                  </H4>
                  <H5 s="2XL" fw="bolder" ff="Proxima Nova" c="surface-brand">
                    99,617
                  </H5>
                </BarTitleBox>
                <PayoutBar />
              </Box>
              <Box jsb>
                <Span s="L" fw="semibold" c="text-secondary">
                  Regular Pool 79,694
                </Span>
                <Span s="L" fw="semibold" c="text-secondary">
                  Bonus Pool 19,923
                </Span>
              </Box>
            </Box>
          </div>
          <div>
            <Box padB="section" dc>
              <VaultCarousel />
              <div
                style={{ paddingBottom: 'var(--Index-Grids-Margins, 48px)' }}
              />
            </Box>
          </div>
        </MaxWidth>
      </HomePageSection>

      <ForceFullWidth>
        <BrandBackground>
          <HomePageSection>
            <Box dc padV="section" g={48} pr={false}>
              <Box dc g={16} c>
                <H2 s="S" c="text-invert">
                  NFTs
                </H2>
                <H3
                  s="3XL"
                  fw="bolder"
                  c="text-invert"
                  ff="Proxima Nova"
                  uc
                  tac
                >
                  Access to Tier 1 NFT Drops
                </H3>
                <Box mw="640px">
                  <P s="XL" c="text-invert" tac>
                    NFT's we’ve periodically airdropped to Tier 1 VAULT holders,
                    all powered by Gelato and verified on-chain randomness
                  </P>
                </Box>
              </Box>
              <ForceFullWidth>
                <NFTDropsCarousel />
              </ForceFullWidth>
            </Box>
          </HomePageSection>
        </BrandBackground>
      </ForceFullWidth>

      <HomePageSection>
        <Box padding="var(--Index-Grids-Margins) 0">
          <Box padding="var(--Index-Grids-Margins) 0" dc g={48}>
            <Box dc c g={16}>
              <H2 s="S" c="text-brandPrimary">
                How it works
              </H2>
              <H3 s="3XL" fw="bolder" c="text-primary" ff="Proxima Nova" uc tac>
                More value, more rewards
              </H3>
              <P s="XL" c="text-primary" tac>
                Your VAULT unlocks vast value across the entire ZAP ecosystem
              </P>
            </Box>
            <ChartBox c relative z={1000}>
              <img
                className="desktop-chart"
                src="/zap-vault-chart.svg"
                width="var(--Index-Grids-Max-Width, 1440px)"
                height="747px"
                alt="ZAP Vault Ecosystem"
              />
              <img
                className="mobile-chart"
                src="/zap-vault-chart-mobile.svg"
                width="2105px"
                height="375px"
                alt="ZAP Vault Ecosystem"
              />
            </ChartBox>
          </Box>
        </Box>
      </HomePageSection>

      <ForceFullWidth>
        <BrandBackground>
          <MaxWidth maxWidth={1440}>
            <Box padV="section">
              <ProductsHero invertColors tertiary={false} />
            </Box>
          </MaxWidth>
        </BrandBackground>
      </ForceFullWidth>
    </>
  )
}

export function Countdown({
  endDate,
  $color,
  showDays = true,
}: {
  endDate: Date
  $color?: string
  showDays?: boolean
}) {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const difference = endDate.getTime() - now.getTime()

  const days = Math.floor(difference / (1000 * 60 * 60 * 24))
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((difference / 1000 / 60) % 60)
  const seconds = Math.floor((difference / 1000) % 60)

  const hasDaysEnded = days <= 0
  const hasHoursEnded = hasDaysEnded && hours <= 0
  const hasMinutesEnded = hasHoursEnded && minutes <= 0
  const hasSecondsEnded = hasMinutesEnded && seconds <= 0

  return (
    <Box g={8} fu aw>
      {showDays && (
        <CountdownItem
          label="Days"
          value={days}
          hasEnded={hasDaysEnded}
          $color={$color}
        />
      )}
      <CountdownItem
        label="Hours"
        value={hours}
        hasEnded={hasHoursEnded}
        $color={$color}
      />
      <CountdownItem
        label="Minutes"
        value={minutes}
        hasEnded={hasMinutesEnded}
        $color={$color}
      />
      <CountdownItem
        label="Seconds"
        value={seconds}
        hasEnded={hasSecondsEnded}
        $color={$color}
      />
    </Box>
  )
}

function CountdownItem({
  value,
  label,
  hasEnded,
  $color,
}: {
  value: number
  label: string
  hasEnded: boolean
  $color?: string
}) {
  return (
    <CountdownItemCont dc jc ac g={8} fu aw>
      <CountdownValue hasEnded={hasEnded} $color={$color}>
        {hasEnded
          ? 0
          : value.toLocaleString('en-GB', { minimumIntegerDigits: 2 })}
      </CountdownValue>
      <CountdownLabel>{label}</CountdownLabel>
    </CountdownItemCont>
  )
}

function VaultsSold({ total }: { total: number }) {
  return (
    <Box ac g={12} fu aw fw>
      <Box g={8} fu aw>
        {total
          .toString()
          .split('')
          .map((char, index) => (
            <VaultCountItemCont key={index} dc jc ac g={8} fu aw>
              <VaultCountValue>
                <TickUpNumber amount={Number(char)} />
              </VaultCountValue>
            </VaultCountItemCont>
          ))}
      </Box>
      <VaultsSoldText>Sold</VaultsSoldText>
    </Box>
  )
}

export function TokensAmount({
  isTbc,
  total,
}: {
  isTbc?: boolean
  total: number
}) {
  const totalStr = isTbc ? 'TBC' : total.toLocaleString('en-GB')
  return (
    <Box ac g={12} fu aw fw>
      <CountsCont g={8} fu aw>
        {totalStr.split('').map((char, index) => {
          if (!isTbc && !Number.isInteger(Number(char))) {
            return (
              <Box
                key={`${char}-${index}`}
                alignSelf="end"
                marginLeft={-6}
                marginRight={-6}
              >
                <Span s="4XL" fw="semibold" c="text-invert">
                  {char}
                </Span>
              </Box>
            )
          }
          return (
            <VaultCountItemCont key={`${char}-${index}`} dc jc ac g={8} fu aw>
              <VaultCountValue>
                {isTbc ? char : <TickUpNumber amount={Number(char)} />}
              </VaultCountValue>
            </VaultCountItemCont>
          )
        })}
      </CountsCont>
    </Box>
  )
}

export function VaultsAPY({
  isTbc,
  value,
}: {
  isTbc?: boolean
  value: string
}) {
  const valueStr = isTbc ? 'TBC' : value
  return (
    <Box ac g={12} fu aw fw>
      <CountsCont g={8} fu aw>
        {valueStr.split('').map((char, index) => {
          if (!isTbc && !Number.isInteger(Number(char)) && char !== '%') {
            return (
              <Box
                key={`${char}-${index}`}
                alignSelf="end"
                marginLeft={-6}
                marginRight={-6}
                style={{
                  position: 'relative',
                  top: '-8px',
                }}
              >
                <Span s="4XL" fw="semibold" c="text-invert">
                  {char}
                </Span>
              </Box>
            )
          }
          return (
            <VaultCountItemCont key={index} dc jc ac g={8} fu aw>
              <VaultCountValue>
                {isTbc ? (
                  char
                ) : char === '%' ? (
                  char
                ) : (
                  <TickUpNumber amount={Number(char)} />
                )}
              </VaultCountValue>
            </VaultCountItemCont>
          )
        })}
        <Box>
          <VaultCountItemCont dc jc ac g={8} fu aw>
            <VaultCountValue>APY</VaultCountValue>
          </VaultCountItemCont>
        </Box>
      </CountsCont>
    </Box>
  )
}

const CountsCont = styled(Box)`
  @media (max-width: 768px) {
    gap: 4px;
  }
`

function VaultsToNextPrice({ total }: { total: number }) {
  // The first 10000 vaults are all at the same price
  // Then after that it’s steps of 200
  // If 5000 sold, 5000 vaults left at this price
  // So if 11150 vaults sold, 50 vaults left at this price
  const totalLeftToNextPrice =
    total < 10000 ? 10000 - total : 200 - (total % 200)

  return (
    <VaultsToNextPriceBox dc afe g={20}>
      <VaultsToNextPriceText>
        Vaults until next price step
      </VaultsToNextPriceText>
      <Box jfe g={8} fu aw>
        {totalLeftToNextPrice
          .toString()
          .split('')
          .map((char, index) => (
            <CountdownItemCont key={index} dc jc ac g={8} fu aw mw="80px">
              <CountdownValue color="#fff">
                <TickUpNumber amount={Number(char)} />
              </CountdownValue>
            </CountdownItemCont>
          ))}
      </Box>
    </VaultsToNextPriceBox>
  )
}

function MetricItem({
  title,
  text,
  value,
  currency,
}: {
  title: string
  text?: string
  value: ReactNode
  currency?: string
}) {
  return (
    <StyledMetricItem ac g={16} padV={24} padH="l">
      <Box dc g={4}>
        <H5 s="M" fw="semibold">
          {title}
        </H5>
        {text && (
          <Span s="M" fw="regular">
            {text}
          </Span>
        )}
      </Box>
      <Box ac g={10} fu aw>
        <H6 s="XL" c="text-brandPrimary" fw="bolder" ff="Proxima Nova">
          {value}
        </H6>
      </Box>
    </StyledMetricItem>
  )
}

function VaultItem({
  imgUrl,
  videoUrl,
  title,
  text,
}: {
  imgUrl?: string
  videoUrl?: string
  title: string
  text: string
}) {
  return (
    <VaultItemBox dc g={20}>
      {!videoUrl && imgUrl && <img src={imgUrl} alt={title} />}
      {videoUrl && (
        <video autoPlay loop muted playsInline>
          <source src={videoUrl} type="video/mp4" />
          {imgUrl && <img src={imgUrl} alt={title} />}
        </video>
      )}
      <Box dc g={12}>
        <VaultItemTitle>{title}</VaultItemTitle>
        <VaultItemText>{text}</VaultItemText>
      </Box>
    </VaultItemBox>
  )
}

function NFTDropsCarousel() {
  const { data: nftDropData } = useGetNftDropsForGuestQuery({})
  const allNftDrops = useMemo(() => nftDropData?.data ?? [], [nftDropData])
  const { width: windowWidth } = useWindowSize()
  const isMobile = windowWidth < 1250
  return (
    <div>
      <Slider
        width={isMobile ? '692px' : '300px'}
        duration={400}
        pauseOnHover={true}
        blurBorders={false}
      >
        {allNftDrops.map((nft: NftDrop) => (
          <div style={{ marginRight: 16 }}>
            <NftDropCard drop={nft} isGuest />
          </div>
        ))}
      </Slider>
    </div>
  )
}

function VaultCarousel() {
  const SLIDES = ['1', '2', '3', '4']

  const [currentSlide, setCurrentSlide] = useState('1')

  const onSwipeLeft = useCallback(() => {
    const currentIndex = SLIDES.indexOf(currentSlide)
    const nextIndex = (currentIndex + 1) % SLIDES.length
    setCurrentSlide(SLIDES[nextIndex])
  }, [currentSlide])
  const onSwipeRight = useCallback(() => {
    const currentIndex = SLIDES.indexOf(currentSlide)
    const prevIndex = (currentIndex - 1 + SLIDES.length) % SLIDES.length
    setCurrentSlide(SLIDES[prevIndex])
  }, [currentSlide])
  const sliderProps = useMemo(
    () => getVaultsSliderProps({ currentSlide }),
    [currentSlide],
  )

  const getIsActiveSlide = useCallback(
    (index: number) => {
      return SLIDES.indexOf(currentSlide) === index
    },
    [currentSlide],
  )

  const onIndicatorClick = useCallback(
    (index: number) => {
      setCurrentSlide(SLIDES[index])
    },
    [currentSlide],
  )

  return (
    <HomeHero
      sliderEnabled
      onSwipeRight={onSwipeRight}
      onSwipeLeft={onSwipeLeft}
      onIndicatorClick={onIndicatorClick}
      getIsActiveSlide={getIsActiveSlide}
      {...sliderProps}
      heroLeft={<HeroLeft currentSlide={currentSlide} />}
    />
  )
}

export function HeroLeft({ currentSlide }: { currentSlide: string }) {
  const imgSrc = {
    1: '/video/zap-vault-1-fallback.png',
    2: '/video/zap-vault-2-fallback.png',
    3: '/zap-vault-3.png',
    4: '/zap-vault-4.jpg',
  }[currentSlide]
  const videoSrc = {
    1: '/video/zap-vault-1.mp4',
    2: '/video/zap-vault-2.mp4',
  }[currentSlide]

  const [hasLoaded, setHasLoaded] = useState(false)

  console.log({ videoSrc, hasLoaded })
  useEffect(() => {
    setHasLoaded(false)
  }, [videoSrc])

  const VideoBlock = useCallback(() => {
    return (
      <VaultItemVideo autoPlay muted loop playsInline>
        <source src={videoSrc} type="video/mp4" />
        {imgSrc && <img src={imgSrc} alt="vault item" />}
      </VaultItemVideo>
    )
  }, [videoSrc, imgSrc])

  return (
    <RightSectionContainer>
      {!!videoSrc ? (
        <VideoBlock />
      ) : (
        <VaultItemImageCont>
          <img src={imgSrc} alt="vault item" />
        </VaultItemImageCont>
      )}
    </RightSectionContainer>
  )
}

const RightSectionContainer = styled(Box)`
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    min-height: 334px;
  }
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    min-height: 334px;
    max-height: 334px;
  }
`
const LoadingContainer = styled(Box)`
  height: 100%;
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    min-height: 334px;
  }
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    min-height: 334px;
    max-height: 334ppx;
  }
`

const VaultItemVideo = styled.video`
  object-fit: cover;
  height: 334px;
  width: 100%;
`

const VaultItemImageCont = styled.div`
  border-radius: var(--units-12, 12px);
  width: 100%;

  img {
    height: 334px;
    width: 100%;
    object-fit: cover;
  }
`

function getVaultsSliderProps({ currentSlide }) {
  const slideToSmallTitle = {
    1: "What's inside?",
    2: "What's inside?",
    3: "What's inside?",
    4: "What's inside?",
  }
  const slideToTitle = {
    1: 'Daily $ZAP rewards',
    2: 'Enhanced staking rewards',
    3: 'Additional mystery airdrops',
    4: 'Exclusive platform benefits',
  }
  const slideToDescription = {
    1: 'Receive $ZAP tokens, every day. Your reward amount stays unchanged for the first year before halving every two years after. $ZAP holders can claim their daily rewards in their ZAP VAULT dashboard.',
    2: 'ZAP Vault stakers will receive amplified benefits compared to regular stakers. From ecosystem benefits to launch partner rewards, Vault holders are supercharged.',
    3: 'ZAP has partnered with your favourite NFT and crypto projects. ZAP Vaults come with winning tickets to high-value NFTs and project rewards in periodic lotteries.',
    4: 'ZAP Vaults unlock early access to new features, discounts, and periodic power-ups.',
  }

  const _smallTitle = slideToSmallTitle[currentSlide]
  const _title = slideToTitle[currentSlide]
  const _description = slideToDescription[currentSlide]

  return {
    slidesCount: 4,
    smallTitle: _smallTitle,
    title: _title,
    description: _description,
  }
}
